import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Divider,
  Autocomplete,
  Avatar,
  Chip,
  FormControlLabel,
  Switch,
  alpha,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { X, Send, User, AlertTriangle } from 'lucide-react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { useAppSelector } from '@/store';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { TaskPriority, DelegatedTask, DelegatedTaskStatus } from '@/types';
import { delegatedTaskService, userService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';

// Form validation schema
const delegatedTaskSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200, 'Title too long'),
  description: z.string().max(2000, 'Description too long').optional(),
  priority: z.nativeEnum(TaskPriority),
  assignedTo: z.string().min(1, 'Please select a person to delegate to'),
  startDate: z.any().nullable().optional(),
  dueDate: z.any().nullable(),
  estimatedMinutes: z.number().min(0, 'Must be positive').optional(),
  notes: z.string().max(1000, 'Notes too long').optional(),
  isUrgent: z.boolean().optional(),
  tags: z.array(z.string()).optional(),
});

type DelegatedTaskFormData = z.infer<typeof delegatedTaskSchema>;

interface CreateDelegatedTaskModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const priorityOptions = [
  { value: TaskPriority.P1, label: 'P1 - Critical', color: '#ef4444' },
  { value: TaskPriority.P2, label: 'P2 - High', color: '#f59e0b' },
  { value: TaskPriority.P3, label: 'P3 - Medium', color: '#2563eb' },
  { value: TaskPriority.P4, label: 'P4 - Low', color: '#94a3b8' },
];

/**
 * CreateDelegatedTaskModal
 * Modal for creating a new delegated task (one-on-one assignment)
 */
const CreateDelegatedTaskModal: React.FC<CreateDelegatedTaskModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const currentUser = useAppSelector(selectCurrentUser);

  // Fetch users for assignment dropdown from API
  const { data: usersData } = useQuery({
    queryKey: QUERY_KEYS.users,
    queryFn: () => userService.getUsers(undefined, { pageSize: 100 }),
    enabled: open,
  });

  const users = usersData ?? [];

  // Filter out current user from assignable users
  const assignableUsers = users.filter(u => u.id !== currentUser?.id);

  // Form setup
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<DelegatedTaskFormData>({
    resolver: zodResolver(delegatedTaskSchema),
    defaultValues: {
      title: '',
      description: '',
      priority: TaskPriority.P3,
      assignedTo: '',
      startDate: dayjs(),
      dueDate: dayjs().add(3, 'day'),
      estimatedMinutes: undefined,
      notes: '',
      isUrgent: false,
      tags: [],
    },
  });

  const isUrgent = watch('isUrgent');

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      reset({
        title: '',
        description: '',
        priority: TaskPriority.P3,
        assignedTo: '',
        startDate: dayjs(),
        dueDate: dayjs().add(3, 'day'),
        estimatedMinutes: undefined,
        notes: '',
        isUrgent: false,
        tags: [],
      });
    }
  }, [open, reset]);

  // Create delegated task mutation
  const createMutation = useMutation({
    mutationFn: (data: Partial<DelegatedTask>) => delegatedTaskService.createDelegatedTask(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.delegatedTasks });
      handleClose();
      onSuccess?.();
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = (data: DelegatedTaskFormData) => {
    const selectedUser = users.find(u => u.id === data.assignedTo);
    
    const payload: Partial<DelegatedTask> = {
      title: data.title,
      description: data.description || '',
      priority: data.priority,
      status: DelegatedTaskStatus.PENDING,
      assignedTo: data.assignedTo,
      assignedToName: selectedUser?.displayName || [selectedUser?.firstName, selectedUser?.lastName].filter(Boolean).join(' ') || 'Unknown',
      assignedBy: currentUser?.id || '',
      assignedByName: currentUser?.displayName || 'Unknown',
      startDate: data.startDate ? dayjs(data.startDate).format('YYYY-MM-DD') : undefined,
      dueDate: dayjs(data.dueDate).format('YYYY-MM-DD'),
      estimatedMinutes: data.estimatedMinutes,
      notes: data.notes,
      isUrgent: data.isUrgent,
      tags: data.tags,
    };

    createMutation.mutate(payload);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Send size={20} style={{ color: '#3b82f6' }} />
              <Typography variant="h6" fontWeight={600}>
                Delegate New Task
              </Typography>
            </Box>
            <IconButton onClick={handleClose} size="small">
              <X size={20} />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />

        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {/* Urgent Flag */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: isUrgent ? alpha('#ef4444', 0.1) : 'grey.50',
                  border: '1px solid',
                  borderColor: isUrgent ? alpha('#ef4444', 0.3) : 'divider',
                }}
              >
                <AlertTriangle size={18} style={{ color: isUrgent ? '#ef4444' : '#9ca3af' }} />
                <Controller
                  name="isUrgent"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          {...field}
                          checked={field.value}
                          color="error"
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2" fontWeight={500}>
                          Mark as Urgent
                        </Typography>
                      }
                      sx={{ m: 0, flex: 1 }}
                    />
                  )}
                />
              </Box>

              {/* Title */}
              <Controller
                name="title"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Task Title"
                    fullWidth
                    required
                    error={!!errors.title}
                    helperText={errors.title?.message}
                    placeholder="What needs to be done?"
                  />
                )}
              />

              {/* Description */}
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Description"
                    fullWidth
                    multiline
                    rows={3}
                    error={!!errors.description}
                    helperText={errors.description?.message}
                    placeholder="Provide details about the task..."
                  />
                )}
              />

              {/* Assign To */}
              <Controller
                name="assignedTo"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    options={assignableUsers}
                    getOptionLabel={(option) => option.displayName || [option.firstName, option.lastName].filter(Boolean).join(' ') || option.email}
                    value={assignableUsers.find(u => u.id === field.value) || null}
                    onChange={(_, newValue) => field.onChange(newValue?.id || '')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Delegate To"
                        required
                        error={!!errors.assignedTo}
                        helperText={errors.assignedTo?.message || 'Select the person to assign this task to'}
                        placeholder="Search for a person..."
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.875rem' }}>
                          {(option.displayName || option.firstName || option.email || '?').charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {option.displayName || [option.firstName, option.lastName].filter(Boolean).join(' ') || option.email}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.email}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  />
                )}
              />

              {/* Priority */}
              <Controller
                name="priority"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.priority}>
                    <InputLabel>Priority</InputLabel>
                    <Select {...field} label="Priority">
                      {priorityOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: option.color,
                              }}
                            />
                            {option.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.priority && (
                      <FormHelperText>{errors.priority.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />

              {/* Dates Row */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                {/* Start Date */}
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field }) => (
                    <DateTimePicker
                      label="Start Date"
                      value={field.value}
                      onChange={field.onChange}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'medium',
                        },
                      }}
                    />
                  )}
                />

                {/* Due Date */}
                <Controller
                  name="dueDate"
                  control={control}
                  render={({ field }) => (
                    <DateTimePicker
                      label="Due Date"
                      value={field.value}
                      onChange={field.onChange}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'medium',
                          required: true,
                          error: !!errors.dueDate,
                          helperText: errors.dueDate?.message as string,
                        },
                      }}
                    />
                  )}
                />
              </Box>

              {/* Estimated Hours */}
              <Controller
                name="estimatedMinutes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Estimated Hours"
                    type="number"
                    fullWidth
                    value={field.value || ''}
                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                    error={!!errors.estimatedMinutes}
                    helperText={errors.estimatedMinutes?.message || 'Optional: How long do you expect this to take?'}
                    inputProps={{ min: 0, step: 0.5 }}
                  />
                )}
              />

              {/* Notes for Assignee */}
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Notes for Assignee"
                    fullWidth
                    multiline
                    rows={2}
                    error={!!errors.notes}
                    helperText={errors.notes?.message || 'Any additional instructions or context'}
                    placeholder="E.g., Please prioritize the mobile view..."
                  />
                )}
              />
            </Box>
          </DialogContent>

          <Divider />

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleClose} color="inherit">
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting || createMutation.isPending}
              startIcon={<Send size={16} />}
            >
              {createMutation.isPending ? 'Delegating...' : 'Delegate Task'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </LocalizationProvider>
  );
};

export default CreateDelegatedTaskModal;
